import styled from 'styled-components';

import { CUST_ANIM, CUST_REDUCED_MOTION } from '../../../../theme/customizer-tokens';

/** Slide direction for an entering step: forward from the right, back from the
 *  left, none (fade only) for the first step painted on a fresh load. */
export type StepSlideDir = 'forward' | 'back' | 'none';

const slideOffset = (dir: StepSlideDir): number => {
  if (dir === 'forward') return CUST_ANIM.STEP_SLIDE_OFFSET_PX; // new step arrives from the right
  if (dir === 'back') return -CUST_ANIM.STEP_SLIDE_OFFSET_PX; // arrives from the left
  return 0; // fade only
};

/**
 * Wraps the active desktop step's body so it slides + fades IN on each step change
 * (driven by `DesktopStepSlide`). Same timing / offset / easing tokens as the
 * mobile `MobileStepSlide`, so the two flows animate identically. Slide-in only —
 * the previous step unmounts with its route, so there is no outgoing layer to
 * animate (the area's lightweight pattern; the fixed progress sidebar + header +
 * footer live OUTSIDE this wrapper, so only the step body travels).
 *
 * It stands in for the step body's box inside `CenterCol`: full width, growing to
 * fill the column (the bodies are `width: 100%`, some `height: 100%`), centring its
 * child exactly as `CenterCol` did. The resting transform is `none` so it leaves no
 * transform containing block once settled; reduced motion drops the horizontal
 * travel.
 */
export const Slide = styled.div<{ $dir: StepSlideDir; $entered: boolean }>`
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  will-change: transform, opacity;
  transition:
    transform ${CUST_ANIM.STEP_SLIDE_MS}ms ${CUST_ANIM.SCREEN_EASE},
    opacity ${CUST_ANIM.STEP_SLIDE_MS}ms ${CUST_ANIM.SCREEN_EASE};
  opacity: ${({ $entered }) => ($entered ? 1 : 0)};
  transform: ${({ $dir, $entered }) => ($entered ? 'none' : `translate3d(${slideOffset($dir)}px, 0, 0)`)};

  ${CUST_REDUCED_MOTION} {
    transition: opacity ${Math.round(CUST_ANIM.STEP_SLIDE_MS / 2)}ms ${CUST_ANIM.SCREEN_EASE};
    transform: none;
  }
`;
